import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ChevronDown, Users } from 'lucide-react';
import { useCountUp } from '../hooks/useCountUp';
export default function HeroSection() {
  const {
    count,
    ref
  } = useCountUp(847, 2000, true);
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.6
      }
    }
  };
  const itemFade = {
    hidden: {
      opacity: 0,
      y: 12,
      filter: 'blur(4px)'
    },
    show: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        duration: 0.7,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };
  const itemHighlight = {
    hidden: {
      opacity: 0,
      scale: 1.08,
      filter: 'blur(6px)'
    },
    show: {
      opacity: 1,
      scale: 1,
      filter: 'blur(0px)',
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };
  const lines = [{
    text: 'Son las 6pm.',
    variant: itemFade
  }, {
    text: 'Abres el refri. Lo cierras.',
    variant: itemFade
  }, {
    text: 'No porque no haya comida… sino porque no hay decisión.',
    variant: itemFade
  }, {
    text: 'Y en ese segundo tu mente empieza a correr:',
    variant: itemFade
  }, {
    text: '«¿Qué hago rápido?»',
    variant: itemHighlight,
    highlight: true
  }, {
    text: '«¿Qué hay?»',
    variant: itemHighlight,
    highlight: true
  }, {
    text: '«¿Otra vez voy a gastar?»',
    variant: itemHighlight,
    highlight: true
  }, {
    text: '«¿Por qué no puedo ser constante?»',
    variant: itemHighlight,
    highlight: true
  }, {
    text: 'No te falta disciplina.',
    variant: itemFade
  }, {
    text: 'Te falta una cosa que casi nadie te da:',
    variant: itemFade
  }, {
    text: 'un sistema que te quite decisiones.',
    variant: itemFade,
    glow: true
  }];
  const scrollToPricing = () => {
    document.getElementById('comprar')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative min-h-screen flex flex-col justify-center items-center bg-gradient-to-b from-brand-cream via-brand-cream to-white overflow-hidden pt-6 pb-16 px-4 sm:px-6 lg:px-8 vignette" data-id="element-147">
      {/* Atmospheric particles */}
      {[...Array(8)].map((_, i) => <motion.div key={i} className="absolute rounded-full pointer-events-none" style={{
      width: 4 + Math.random() * 6,
      height: 4 + Math.random() * 6,
      background: i % 2 === 0 ? 'rgba(123, 63, 160, 0.2)' : 'rgba(196, 129, 58, 0.15)',
      filter: 'blur(1px)',
      left: `${10 + Math.random() * 80}%`,
      top: `${10 + Math.random() * 80}%`
    }} animate={{
      y: [0, -30 - Math.random() * 20, 0],
      x: [0, 10 - Math.random() * 20, 0],
      opacity: [0.1, 0.4, 0.1],
      scale: [1, 1.3, 1]
    }} transition={{
      duration: 4 + Math.random() * 4,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: Math.random() * 3
    }} data-id="element-148" />)}

      {/* Soft atmospheric glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-brand-purple/[0.04] blur-[120px] rounded-full pointer-events-none" data-id="element-149" />
      <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-brand-amber/[0.05] blur-[100px] rounded-full pointer-events-none" data-id="element-150" />

      <div className="relative z-10 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-151">
        <div className="order-1" data-id="element-152">
          <motion.div initial={{
          opacity: 0,
          y: 40,
          filter: 'blur(10px)'
        }} animate={{
          opacity: 1,
          y: 0,
          filter: 'blur(0px)'
        }} transition={{
          duration: 1.2,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} className="mb-8" data-id="element-153">
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl leading-tight text-slate-900 mb-2 uppercase tracking-wide" data-id="element-154">
              A las 6pm tu cerebro se apaga.
            </h1>
            <motion.p initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.5,
            duration: 1,
            ease: 'easeOut'
          }} className="font-heading text-3xl sm:text-4xl text-brand-purple italic tracking-wide" data-id="element-155">
              Esto ya lo decidió por ti.
            </motion.p>
          </motion.div>

          <motion.div variants={container} initial="hidden" animate="show" className="space-y-3 mb-10" data-id="element-156">
            {lines.map((line, index) => <motion.p key={index} variants={line.variant} className={`text-lg sm:text-xl leading-relaxed ${line.highlight ? 'text-brand-amber font-medium italic' : line.glow ? 'text-slate-900 font-bold' : 'text-slate-500'}`} data-id="element-157">
                {line.text}
              </motion.p>)}
          </motion.div>

          {/* Image - MOBILE ONLY */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.85,
          filter: 'blur(15px)'
        }} animate={{
          opacity: 1,
          scale: 1,
          filter: 'blur(0px)'
        }} transition={{
          duration: 1.4,
          delay: 0.5,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} className="flex justify-center mb-8 lg:hidden" data-id="element-158">
            <div className="relative animate-float" data-id="element-159">
              <div className="absolute -inset-6 bg-brand-purple/8 blur-[50px] rounded-full" data-id="element-160" />
              <img src="https://cdn.magicpatterns.com/uploads/tVL8rhQXJB4ebJ8P9oet2p/Mock_up_lading...png" alt="CERO DECISIONES Sistema Mockup" className="relative w-full max-w-sm h-auto drop-shadow-2xl rounded-lg" data-id="element-161" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30,
          filter: 'blur(8px)'
        }} animate={{
          opacity: 1,
          y: 0,
          filter: 'blur(0px)'
        }} transition={{
          delay: 2.8,
          duration: 0.8,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} className="flex flex-col gap-3 items-start" data-id="element-162">
            <Button size="xl" pulse={true} onClick={scrollToPricing} data-id="element-163">
              SÍ, QUIERO MI CALMA DE VUELTA →
            </Button>

            <div className="flex flex-col gap-1 ml-2" data-id="element-164">
              <span className="text-slate-500 text-sm" data-id="element-165">
                Por solo $6.39 — Acceso inmediato
              </span>
              <span className="text-slate-400 text-xs" data-id="element-166">
                ✓ Si no funciona, te devolvemos todo en 7 días. Sin preguntas.
              </span>
            </div>

            {/* Social Proof Counter */}
            <div className="flex items-center gap-2 mt-3 bg-brand-cream/80 backdrop-blur-sm px-4 py-2 rounded-full border border-brand-purple/15" ref={ref} data-id="element-167">
              <Users size={16} className="text-brand-purple" data-id="element-168" />
              <span className="text-slate-600 text-sm font-medium" data-id="element-169">
                <span className="font-bold text-brand-purple text-lg mr-1 tabular-nums" data-id="element-170">
                  {count}
                </span>
                mamás ya recuperaron su calma
              </span>
            </div>
          </motion.div>
        </div>

        {/* Image - DESKTOP ONLY */}
        <motion.div initial={{
        opacity: 0,
        x: 60,
        scale: 0.9,
        filter: 'blur(15px)'
      }} animate={{
        opacity: 1,
        x: 0,
        scale: 1,
        filter: 'blur(0px)'
      }} transition={{
        duration: 1.5,
        delay: 0.3,
        ease: [0.25, 0.46, 0.45, 0.94]
      }} className="order-2 hidden lg:flex justify-center" data-id="element-171">
          <div className="relative animate-float" data-id="element-172">
            <div className="absolute -inset-8 bg-brand-purple/8 blur-[60px] rounded-full" data-id="element-173" />
            <img src="https://cdn.magicpatterns.com/uploads/tVL8rhQXJB4ebJ8P9oet2p/Mock_up_lading...png" alt="CERO DECISIONES Sistema Mockup" className="relative w-full max-w-md lg:max-w-full h-auto drop-shadow-2xl rounded-lg transform rotate-1 hover:rotate-0 transition-transform duration-700" data-id="element-174" />
          </div>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1,
      y: [0, 10, 0]
    }} transition={{
      delay: 3.5,
      duration: 2.5,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-brand-purple/30" data-id="element-175">
        <ChevronDown size={32} data-id="element-176" />
      </motion.div>
    </section>;
}